// App.js
//  * Main application component for the unsubscribe page

import React, { useState, useEffect, Fragment } from "react";
import { Card, Text, TextInput, Button, Divider, Grid } from "@tremor/react";
import { Listbox, Transition } from "@headlessui/react";
import { ChevronUpDownIcon } from "@heroicons/react/20/solid";
import { useTranslation } from "react-i18next";
import "./i18n";

const submitUrl = "../../controller/suscription.controller.php";
//const submitUrl = "https://services.danahosting.net/tools/unsubscribe/mock-server.php";

function getQueryParam() {
  const params = new URLSearchParams(window.location.search);
  if (params.has("z")) return { key: "z", value: params.get("z") };
  if (params.has("p")) return { key: "p", value: params.get("p") };
  if (params.has("idcontact")) return { key: "idcontact", value: params.get("idcontact") };
  return null;
}

function CustomDropdown({ options, selected, setSelected, placeholder }) {
  return (
    <Listbox value={selected} onChange={setSelected}>
      <div className="relative mt-1">
        <Listbox.Button className="relative w-full cursor-pointer rounded-md bg-white py-2 pl-3 pr-10 text-left shadow-sm border border-gray-300 text-sm text-black">
          <span className="block truncate">{selected || placeholder}</span>
          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
            <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
          </span>
        </Listbox.Button>
        <Transition as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
          <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5">
            {options.map((option, idx) => (
              <Listbox.Option
                key={idx}
                value={option}
                className={({ active }) =>
                  `relative cursor-pointer select-none py-2 pl-4 pr-4 ${active ? "bg-blue-100 text-blue-900" : "text-gray-900"}`
                }
              >
                {option}
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </Transition>
      </div>
    </Listbox>
  );
}

export default function App() {
  const { t, i18n } = useTranslation();

  const [customFrequency, setCustomFrequency] = useState("");
  const unsubscribeReasons = t("unsubscribe.reasons", { returnObjects: true });
  const frequencyOptions = t("unsubscribe.frequencies", { returnObjects: true });
  const contactMethodOptions = t("unsubscribe.contact_methods", { returnObjects: true });

  const [selectedReason, setSelectedReason] = useState("");
  const [otherReason, setOtherReason] = useState("");
  const [selectedServices, setSelectedServices] = useState([]);
  const [unsubscribeOption, setUnsubscribeOption] = useState("todos");
  const [frequency, setFrequency] = useState("");
  const [contactMethod, setContactMethod] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [paramError, setParamError] = useState(false);
  const [showConfirmationStep, setShowConfirmationStep] = useState(false);
  const [hasLegalNotice, setHasLegalNotice] = useState(false);
  const [hasConfirmationStep, setHasConfirmationStep] = useState(false);
  const [hideServices, setHideServices] = useState(false);

  const otherFrequencyLabel = frequencyOptions[3];

  const [fetchedParams, setFetchedParams] = useState({
    idConversation: 0,
    idCliente: 0,
    idChannel: 0,
    emailAddress: "",
    companyName: "",
    companyLogoUrl: "",
    servicesList: [],
    contactkey: "",
    unsubscribemethod: ""
  });
  

  
  useEffect(() => {
    const queryParam = getQueryParam();
    if (!queryParam) {
      setParamError(true);
      return;
    }

    const fetchUrl = `get_parameters.php?${queryParam.key}=${queryParam.value}`;
    fetch(fetchUrl)
      .then((res) => res.json())
      .then((data) => {

        if (
          data.result !== "success" ||
          typeof data.idConversation !== "number" ||
          typeof data.idCliente !== "number" ||
          typeof data.idChannel !== "number" ||
          !data.emailAddress
        ) {
          setParamError(true);
          return;
        }

        let parsedServices = {};
        let blockedIds = [];

        try {
          parsedServices = JSON.parse(data.servicesList);
          blockedIds = JSON.parse(data.blockedList || "[]");
        } catch (e) {
          setParamError(true);
          return;
        }

        const servicesArray = Object.entries(parsedServices).map(([id, svc]) => ({
          id,
          name: svc.name,
          checked: blockedIds.includes(id)
        }));

        // Handle pageinfo with flexible parsing and support for synonyms
        let pageinfoRaw = data.pageinfo || "";
        try {
          if (typeof pageinfoRaw === "string" && pageinfoRaw.trim().startsWith("{")) {
            pageinfoRaw = JSON.parse(pageinfoRaw);
          }
        } catch (e) {
          console.warn("⚠️ Could not parse pageinfo as JSON:", e);
        }

        const cleanedPageinfo = pageinfoRaw
          .replace(/^{|}$/g, "") // remove curly braces
          .replace(/"/g, "")     // remove quotes
          .toLowerCase();

        const pageinfoFlags = cleanedPageinfo.split(";").map(f => f.trim());

        // Support for synonyms in flags
        setHasConfirmationStep(pageinfoFlags.includes("confirmation") || pageinfoFlags.includes("showconfirmation"));
        setHasLegalNotice(pageinfoFlags.includes("legal") || pageinfoFlags.includes("customresult"));
        setHideServices(pageinfoFlags.includes("hideservices"));

        setFetchedParams({
          ...data,
          servicesList: servicesArray,
          contactkey: data.contactkey || "",
          unsubscribemethod: data.unsubscribemethod || ""
        });

        const preselected = servicesArray.filter(s => s.checked).map(s => s.id);
        setSelectedServices(preselected);
      })
      .catch((err) => {
        console.error("❌ Error loading parameters:", err);
        setParamError(true);
      });
  }, []);

  const isOther = selectedReason === unsubscribeReasons[5];
  const showFrequencyDropdowns = selectedReason === unsubscribeReasons[0];

  const toggleService = (serviceId) => {
    setSelectedServices((prev) =>
      prev.includes(serviceId)
        ? prev.filter((id) => id !== serviceId)
        : [...prev, serviceId]
    );
  };

  const sendPayload = () => {
    const formData = new FormData();
  
    let description = "";
    if (selectedReason === unsubscribeReasons[0]) {
      description = selectedReason;
      if (frequency) {
        const freqValue = frequency === otherFrequencyLabel ? customFrequency : frequency;
        description += `|${freqValue}`;
      }
      if (contactMethod) description += `|${contactMethod}`;
    } else if (
      selectedReason === unsubscribeReasons[1] ||
      selectedReason === unsubscribeReasons[2] ||
      selectedReason === unsubscribeReasons[3] ||
      selectedReason === unsubscribeReasons[4]
    ) {
      description = selectedReason;
    } else if (selectedReason === unsubscribeReasons[5]) {
      description = otherReason;
    }
  
    formData.append("description", description);
    // ✅ Siempre se evalúa contactkey
    const hasContactKey = fetchedParams.contactkey && fetchedParams.contactkey.trim() !== "";
  
    formData.append("action", hasContactKey ? "unsubscribe" : "block");
  
  
    if (hasContactKey) {
      formData.append("contactkey", fetchedParams.contactkey);
    } else {
      formData.append("idCliente", parseInt(fetchedParams.idCliente, 10));
      formData.append("emailAddress", fetchedParams.emailAddress);
    }
  
    formData.append("idConversation", parseInt(fetchedParams.idConversation, 10));
    formData.append("idChannel", parseInt(fetchedParams.idChannel, 10));
    formData.append("isSpam", selectedReason === unsubscribeReasons[1]);
    formData.append("unsuscribeAll", unsubscribeOption === "todos");
    formData.append("block", unsubscribeOption === "todos");
    
  
    if (unsubscribeOption === "algunos") {
      selectedServices.forEach((id) => {
        formData.append("services[]", id);
      });
      fetchedParams.servicesList.forEach((service) => {
        formData.append("unblocked[]", service.id);
      });
    }
  
    setIsSubmitting(true);

    // ✅ Debug del payload
// console.log("📦 Payload being sent:");
// for (let pair of formData.entries()) {
//   console.log(`${pair[0]}: ${pair[1]}`);
// }
  
    fetch(submitUrl, {
      method: "POST",
      body: formData
    })
      .then((res) => {
        if (!res.ok) throw new Error("Error sending form");
        return res.text();
      })
      .then(() => {
        setSubmitted(true);
      })
      .catch(() => {
        alert("There was an error processing your request.");
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };
  

  const handleSubmit = () => {
    if (hasConfirmationStep) {
      setShowConfirmationStep(true);
      return;
    }
    sendPayload();
  };

  if (paramError) {
    return (
      <div className="min-h-screen bg-gray-100 flex flex-col items-center justify-center px-4 py-10">
        <div className="mb-4 text-sm space-x-4 text-blue-500">
          <button onClick={() => i18n.changeLanguage("es")}>Español</button>
          <button onClick={() => i18n.changeLanguage("en")}>English</button>
        </div>
        <Card className="max-w-md w-full p-6 border border-red-300 bg-white shadow-md text-center">
          <h2 className="text-xl font-bold text-red-600 mb-2">{t("error.title")}</h2>
          <Text className="text-gray-700">{t("error.message")}</Text>
        </Card>
      </div>
    );
  }

  if (showConfirmationStep) {
    return (
      <div className="min-h-screen bg-gray-100 flex flex-col items-center justify-center px-4 py-10">
        <div className="flex justify-end space-x-4 mb-2 w-full max-w-md text-sm">
          <button className="text-blue-600 hover:underline" onClick={() => i18n.changeLanguage("es")}>Español</button>
          <button className="text-blue-600 hover:underline" onClick={() => i18n.changeLanguage("en")}>English</button>
        </div>
        <Card className="max-w-md w-full p-6 border border-gray-300 bg-white shadow-md text-center">
          <h2 className="text-xl font-bold text-blue-800 mb-4">{t("unsubscribe.confirmation.title")}</h2>
          <Text className="text-gray-700 mb-6">{t("unsubscribe.confirmation.message")}</Text>
          <div className="flex flex-col space-y-4">
            <Button onClick={() => setShowConfirmationStep(false)} className="bg-gray-300 text-gray-800 w-full rounded-full">
              {t("unsubscribe.confirmation.back_button")}
            </Button>
            <Button onClick={() => { setShowConfirmationStep(false); sendPayload(); }} className="bg-red-600 text-white w-full rounded-full">
              {t("unsubscribe.confirmation.confirm_button")}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4 py-10">
      <div className="flex justify-end space-x-4 mb-2 w-full max-w-md text-sm">
        <button className="text-blue-600 hover:underline" onClick={() => i18n.changeLanguage("es")}>Español</button>
        <button className="text-blue-600 hover:underline" onClick={() => i18n.changeLanguage("en")}>English</button>
      </div>

      <Card className="w-full max-w-md shadow-xl p-8 border border-blue-200 bg-white rounded-2xl">
        <div className="flex justify-center mb-4">
          <img src={fetchedParams.companyLogoUrl} alt="Logo" className="w-auto" />
        </div>

        {!submitted ? (
          <>
            <h2 className="text-2xl font-bold text-blue-700 mb-2 text-center">
              {t("unsubscribe.title", { company: fetchedParams.companyName })}
            </h2>
            <Text className="text-gray-600 text-center mb-4">{t("unsubscribe.apology")}</Text>

            <Text className="font-semibold mb-2">{t("unsubscribe.why")}</Text>
            <CustomDropdown options={unsubscribeReasons} selected={selectedReason} setSelected={setSelectedReason} placeholder={t("unsubscribe.reason_placeholder")} />

            {isOther && (
              <TextInput className="mt-4" placeholder={t("unsubscribe.specify_reason")} value={otherReason} onChange={(e) => setOtherReason(e.target.value)}  maxLength={100} />
            )}

            {showFrequencyDropdowns && (
              <>
                <Divider className="my-4" />
                <CustomDropdown options={frequencyOptions} selected={frequency} setSelected={setFrequency} placeholder={t("unsubscribe.frequency_preference")} />
                {frequency === otherFrequencyLabel && (
                  <TextInput className="mt-4" placeholder={t("unsubscribe.specify_frequency")} value={customFrequency} onChange={(e) => setCustomFrequency(e.target.value)} maxLength={150} />
                )}
                <div className="mt-4">
                  <CustomDropdown options={contactMethodOptions} selected={contactMethod} setSelected={setContactMethod} placeholder={t("unsubscribe.other_contact")} />
                </div>
              </>
            )}

            {!hideServices && (
              fetchedParams.servicesList.length > 0 && (
                <>
                  <Divider className="my-4" />
                  <Text className="font-semibold mb-2">{t("unsubscribe.services_title")}</Text>
                  <label className="block mb-2">
                    <input type="radio" className="mr-2" checked={unsubscribeOption === "todos"} onChange={() => setUnsubscribeOption("todos")} />
                    {t("unsubscribe.no_services")}
                  </label>
                  <label className="block">
                    <input type="radio" className="mr-2" checked={unsubscribeOption === "algunos"} onChange={() => setUnsubscribeOption("algunos")} />
                    {t("unsubscribe.some_services")}
                  </label>
                </>
              )
            )}

            {!hideServices && unsubscribeOption === "algunos" && (
              <div className="mt-4 border p-4 rounded bg-gray-50">
                <Grid numItems={1}>
                  {fetchedParams.servicesList.map((service) => (
                    <label key={service.id} className="flex items-center space-x-2">
                      <input type="checkbox" checked={selectedServices.includes(service.id)} onChange={() => toggleService(service.id)} />
                      <span>{service.name}</span>
                    </label>
                  ))}
                </Grid>
              </div>
            )}

            <Button
              onClick={handleSubmit}
              className="mt-6 w-full bg-blue-600 text-white rounded-full"
              disabled={isSubmitting || !selectedReason || (isOther && !otherReason.trim())}
            >
              {isSubmitting ? "Sending..." : t("unsubscribe.submit")}
            </Button>
          </>
        ) : (
          <div className="text-center">
            {hasLegalNotice ? (
              <div className="text-sm text-gray-700 bg-gray-50 p-4 border border-gray-300 rounded-lg text-left">
                <p className="font-semibold text-gray-800 mb-2">{t("unsubscribe.legal_notice.title")}</p>
                <p className="mb-1">{t("unsubscribe.legal_notice.paragraph1")}</p>
                <p>{t("unsubscribe.legal_notice.paragraph2")}</p>
              </div>
            ) : (
              <Text className="text-green-700 font-semibold text-lg">
                {t("unsubscribe.thank_you", { company: fetchedParams.companyName })}
              </Text>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
